import { action, computed, makeObservable, observable } from "mobx";
import { eventNames } from "./constants";
import DictionaryHelper, { Dictionary, LanguageDictionary, englishLanguageDictionary } from "./dictionary";
import { Country, MenuConfig, MenuSite, NavItem, Settings } from "./types";
import Inbox, { defaultInbox } from "./inbox";
import { AuthService } from "./services/auth.service.interface";
import { CiamService, ConnectService } from "./services";
import { Language } from "./types/language";
import Cart, { defaultCart } from "./cart";
import Wishlist, { defaultWishlist } from "./wishlist";
import environment from "@menu-environment";

export enum DisplayItem {
    MainNav,
    SubNav,
    Channels,
    Search
}

export enum OsPlatform {
    IPhone = "iphone",
    Ipad = "ipad",
    Huawei = "huawei",
    Mac = "mac",
    Windows = "window",
    Android = "android",
}

//React location
type Location = {
    pathname: string
}

type ConnectToken = {
    access: string,
    id: string,
    time: number
}

export type MenuContextOptions = {
    config: MenuConfig;
    siteName: string;
    countries: Country[];
    country: Country;
    culture: Language | undefined;
    skipAuthValidation?: boolean;
}

class MenuContext {
    channels: MenuSite[] = [];
    config: MenuConfig;
    country: Country;
    countryCode = "";
    culture: Language | undefined = undefined;
    countries: Country[] = [];
    dictionary: Dictionary = DictionaryHelper.dictionary;
    displayMobileNav = false;
    displayProfileMenu = false;
    inbox: Inbox = defaultInbox;
    cart: Cart = defaultCart;
    wishlist: Wishlist = defaultWishlist;
    isLoggedIn = false;
    isMobile = false;
    languageCode = "";
    languageDictionary: LanguageDictionary;
    location: Location | undefined = undefined;
    openCountrySelector = false;
    selectedArea: NavItem | undefined = undefined;
    settings: Settings = {} as Settings;
    mainConfig?: MenuConfig;
    site: MenuSite;
    siteName: string;
    connectToken: ConnectToken | undefined = undefined;
    displayItem: DisplayItem = DisplayItem.MainNav;
    skipAuthValidation = false;

    index = 0;

    constructor(options: MenuContextOptions) {
        const { config, siteName, countries, country, culture = undefined, skipAuthValidation = false } = options;
        this.countries = countries;
        this.country = country;
        this.culture = culture;
        this.countryCode = culture?.code || "";
        this.skipAuthValidation = skipAuthValidation;
        this.siteName = siteName;
        this.languageDictionary = DictionaryHelper.getLanguageDictionary(this.languageCode);

        this.config = config
        this.site = this.config.sites[0];
        this.channels = this.config.sites.filter(x => x !== this.site);

        this.inbox = defaultInbox;
        this.cart = defaultCart;
        this.wishlist = defaultWishlist;

        makeObservable(this, {
            channels: observable,
            config: observable,
            connectToken: observable,
            countryCode: observable,
            culture: observable,
            countries: observable,
            dictionary: observable,
            displayItem: observable,
            displayMobileNav: observable,
            inbox: observable,
            cart: observable,
            wishlist: observable,
            isLoggedIn: observable,
            isMobile: observable,
            languageCode: observable,
            languageDictionary: observable,
            location: observable,
            openCountrySelector: observable,
            selectedArea: observable,
            site: observable,
            siteName: observable,

            clearArea: action,
            updateArea: action,
            updateConnectToken: action,
            updateCountry: action,
            updateCulture: action,
            updateDisplayItem: action,
            updateDisplayMobileNav: action,
            updateDisplayProfileMenu: action,
            updateInbox: action,
            updateCart: action,
            updateWishlist: action,
            updateIsLoggedIn: action,
            updateIsMobile: action,
            updateLocation: action,
            updateNav: action,
            updateSettings: action,

            displayChannelMenu: computed,
            displaySearch: computed,
            displaySubMenu: computed,
            isBusinessSite: computed,
            isDesktop: computed,
            isMainSite: computed,
            mainSiteUrl: computed,
            siteUrls: computed,
            siteUrl: computed,
            appOneLink: computed,
        });
    }

    updateNav(value: NavItem[]) {
        this.config.sites[0].navItems = value;

        this.site = this.config.sites[0];

        if (!this.config.sites?.length) {
            return;
        }

        this.channels = this.config.sites.filter(x => x !== this.site);
    }

    updateCountry(value: Country) {
        this.country = value;
    }

    updateCulture(culture: Language | undefined) {
        this.culture = culture;
    }

    updateArea(value: NavItem) {
        this.selectedArea = value;
    }

    clearArea() {
        this.selectedArea = undefined;
    }

    updateDisplayItem(value: DisplayItem) {
        this.displayItem = value;
    }

    updateInbox(value: Inbox) {
        this.inbox = value;
    }

    updateCart(value: Cart) {
        this.cart = value;
    }

    updateWishlist(value: Wishlist) {
        this.wishlist = value;
    }

    updateIsLoggedIn(value: boolean) {
        this.isLoggedIn = value;
    }

    updateIsMobile(isMobile: boolean) {
        this.isMobile = isMobile;
    }

    updateConnectToken(access: string, id: string) {
        const time = new Date().getTime();

        this.connectToken = {
            access,
            id,
            time
        };
    }

    updateDisplayMobileNav(value: boolean, openCountrySelector = false) {
        this.displayMobileNav = value;
        this.openCountrySelector = openCountrySelector;
    }

    updateDisplayProfileMenu(value: boolean) {
        this.displayProfileMenu = value;
    }

    updateSettings(value: Settings) {
        this.settings = value;
    }

    updateMainSettings(value?: MenuConfig) {
        this.mainConfig = value;
    }

    forceUpdate() {
        this.index++;
    }

    updateLocation(value: Location) {
        this.location = value;
    }

    private _shouldDisplay(value: DisplayItem) {
        return this.displayItem === value;
    }

    get displaySubMenu() {
        return this._shouldDisplay(DisplayItem.SubNav);
    }

    get displayChannelMenu() {
        return this._shouldDisplay(DisplayItem.Channels);
    }

    get displaySearch() {
        return this._shouldDisplay(DisplayItem.Search);
    }

    get isMainSite() {
        return this.config.showAsMainSite || !this.siteName;
    }

    get isBusinessSite() {
        return this.siteName.toLowerCase() === "business";
    }

    get isMediaSalesSite() {
        return this.siteName.toLowerCase() === "mediasales";
    }

    get isDesktop() {
        return !this.isMobile;
    }

    get siteUrls() {
        const url = this.mainSiteUrl;

        return {
            business: "/business" + url,
            mediaSales: "/mediasales" + url,
            main: url
        }
    }

    get siteUrl() {
        const urls = this.siteUrls;
        return this.isBusinessSite
            ? urls.business
            : this.isMediaSalesSite
                ? urls.mediaSales
                : urls.main;
    }

    get mainSiteUrl() {
        let url = this.mainConfig?.sites[0]?.link;

        if (!!url) {
            return url;
        }

        url = `${this.country.url || this.culture}`;
        url += url.endsWith("/") ? "" : "/";
        return url.startsWith("/") ? url : `/${url}`;
    }

    static get DefaultInstance() {
        const o = {
            country: {},
            languageDictionary: englishLanguageDictionary,
            dictionary: DictionaryHelper.dictionary,
            config: {},
            site: {},
        };

        return o as MenuContext;
    }

    get authService(): AuthService {
        return this.config.ciamFlag
            ? new CiamService(this)
            : new ConnectService(this);
    }

    get appBanner() {
        return environment.menu?.appBanner ?? {};
    }

    get appOneLink(): string | undefined {
        const uaResponse = navigator.userAgent.toLowerCase();
        const foundPlatform = Object.values(OsPlatform).find((platform) => uaResponse.includes(platform));

        if (!foundPlatform) {
            return;
        }

        const platformsWithoutBanner = [OsPlatform.Windows, OsPlatform.Mac];
        const hideAppBanner = platformsWithoutBanner?.includes(foundPlatform);

        return hideAppBanner ? undefined : this.appBanner.oneLink;
    }
}

export const initializeMenuEvents = (menuContext: MenuContext, determineIfLoggedIn: any = null) => {
    const updateMenu = (e: any) => {
        console.debug("Received menu config event", e);
        menuContext.updateNav(e.detail)
    }

    document.addEventListener(eventNames.config, updateMenu);

    document.addEventListener(eventNames.ciamLogin, (e) => {
        console.debug("Received logged in event", e);
        determineIfLoggedIn(menuContext, e);
    });

    const updateInbox = (e: any) => {
        console.debug("Received inbox event", e);
        menuContext.updateInbox(e.detail);
    }

    console.debug("Adding event listener", eventNames.inbox);
    document.addEventListener(eventNames.inbox, updateInbox);
}

export default MenuContext;